import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/site/SiteLayout";
import Seo from "@/components/site/Seo";
import { API } from "@/lib/api";

export default function MyLibrary() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sent | error
  const [lib, setLib] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/guides/my-library/verify?token=${encodeURIComponent(token)}`)
      .then((r) => r.ok ? r.json() : r.json().then((e) => Promise.reject(e)))
      .then(setLib)
      .catch((e) => setErr(e.detail || "This link has expired. Request a new one below."));
  }, [token]);

  const request = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("sending");
    try {
      await fetch(`${API}/guides/my-library/request`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }) });
      setStatus("sent");
    } catch { setStatus("error"); }
  };

  return (
    <SiteLayout>
      <Seo title="My Library — The Sourcing Atlas" description="Re-download the guides and bundles you own. Enter your email for a secure magic link." path="/guides/my-library" noindex />
      <div className="crumb"><Link to="/">Home</Link> › <Link to="/guides">Guides</Link> › My Library</div>
      <div className="simplepage" data-testid="my-library">
        <h1>My Library</h1>
        {lib ? (
          <div>
            <p style={{ color: "var(--grey)" }}>Signed in as <strong>{lib.email}</strong>{lib.complete_library ? " · Complete Library member" : ""}. Links work for 30 days (max 5 downloads each).</p>
            <div style={{ display: "grid", gap: 10, marginTop: 16 }}>
              {lib.items.length === 0 && <p>No guides on this email yet. <Link to="/guides">Browse the library →</Link></p>}
              {lib.items.map((it) => (
                <a key={it.slug} href={it.url} className="btn btn-line" data-testid={`lib-dl-${it.slug}`}
                   style={{ display: "flex", justifyContent: "space-between", padding: "12px 16px", textAlign: "left" }}>
                  <span>⬇ {it.title}</span>
                  <span className={"tagf " + (it.tag === "free" ? "tf-free" : "tf-paid")} style={{ fontSize: 11 }}>{it.tag.toUpperCase()}</span>
                </a>
              ))}
            </div>
          </div>
        ) : token && !err ? (
          <p>Opening your library…</p>
        ) : (
          <div style={{ maxWidth: 460 }}>
            {err && <p style={{ color: "#D62828", fontSize: 14 }}>{err}</p>}
            <p style={{ color: "var(--grey)" }}>Enter your email and we'll send a secure link to re-download everything you own — no password needed.</p>
            {status === "sent" ? (
              <div style={{ background: "#EAF6EE", border: "1px solid #BEE3CB", borderRadius: 10, padding: 16, color: "#0E6B3C" }} data-testid="lib-sent">
                ✓ Check your inbox — we've emailed your library link (valid 30 minutes).
              </div>
            ) : (
              <form onSubmit={request}>
                <input placeholder="Your email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="lib-email"
                       style={{ width: "100%", border: "1.5px solid var(--line)", borderRadius: 10, padding: "12px 13px", fontSize: 14, marginBottom: 10 }} />
                <button className="btn btn-red" style={{ width: "100%" }} disabled={status === "sending"} data-testid="lib-request">
                  {status === "sending" ? "Sending…" : "Email me my library link"}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </SiteLayout>
  );
}
